import type {
  CommentRow,
  CountryRow,
  ForumRow,
  PostRow,
  ProfileRow,
  WeaponRow,
  WikiHistoryRow,
} from '../../types/db';
import { makeId } from '../../lib/utils';

const now = new Date().toISOString();
export const seedForums: ForumRow[] = [
  {
    id: 'overview',
    name: 'Overview',
    icon: '◎',
    creator: null,
    members: 2400,
    is_section: false,
    created_at: now,
    description: 'The complete live intelligence picture.',
  },
  {
    id: 'airspace',
    name: 'Airspace',
    icon: '✈',
    creator: 'vandal',
    members: 1200,
    is_section: false,
    created_at: now,
    description: 'Airspace notices and tracking.',
  },
  {
    id: 'jamming',
    name: 'Jamming & EW',
    icon: '⚡',
    creator: 'meridian',
    members: 860,
    is_section: false,
    created_at: now,
  },
  {
    id: 'ukraine',
    name: 'Ukraine',
    icon: '◈',
    creator: null,
    members: 1100,
    is_section: true,
    created_at: now,
  },
  {
    id: 'red-sea',
    name: 'Red Sea',
    icon: '◈',
    creator: null,
    members: 740,
    is_section: true,
    created_at: now,
  },
  {
    id: 'cases',
    name: 'Cases',
    icon: '◆',
    creator: null,
    members: 312,
    is_section: false,
    created_at: now,
  },
];
export const seedPost: PostRow = {
  id: 'demo-post',
  forum_id: 'overview',
  author: 'demo-user',
  title: 'Airspace violation near Bornholm — 3 aircraft, heading west',
  body: 'Community radio report. Two-analyst verification is running; confidence increased after a second independent observation.',
  flair: 'FLASH',
  severity: 1,
  region: 'Baltic Sea · 34U',
  source: 'Community radio',
  confidence: 0.82,
  votes: 214,
  status: 'review',
  is_first_report: true,
  media: [],
  created_at: now,
};
export const demoProfile: ProfileRow = {
  id: 'demo-user',
  codename: 'OSPREY',
  level: 3,
  badge: 'Analyst',
  fr_count: 4,
  bio: 'Open-source intelligence analyst.',
  created_at: now,
};
export const seedCountry: CountryRow = {
  id: 'iran',
  name: 'Iran',
  flag: '🇮🇷',
  summary:
    'Islamic Republic of Iran — regional military power with the largest ballistic-missile and drone arsenal in the Middle East. Doctrine centres on deterrence, asymmetric warfare and denial of the Strait of Hormuz.',
  government:
    'Islamic Republic (theocratic presidential system since 1979). The Supreme Leader holds final authority over the armed forces, security and foreign policy. A 290-seat Majlis legislates alongside the Guardian Council and Expediency Council.',
  geography:
    '1,648,195 km², bordered by seven countries, the Caspian Sea, Persian Gulf and Strait of Hormuz. The Zagros and Alborz ranges surround central plateaus and deserts; the population is approximately 90 million.',
  doctrine:
    'Iran follows a deterrence-by-denial doctrine built around strategic depth, survivable missile forces and asymmetric pressure. The regular armed forces defend national territory while the IRGC fields missiles, drones, expeditionary capabilities and a layered network of regional partners. Dispersed launch sites, mobility, hardened infrastructure and swarm tactics are intended to complicate a technologically superior opponent’s targeting cycle.',
  industry:
    'A large state-led defense sector develops and manufactures ballistic and cruise missiles, unmanned aircraft, air-defense systems, armored vehicles and naval craft. Organizations linked to the Ministry of Defense and the IRGC Aerospace Force emphasize domestic substitution, reverse engineering and iterative upgrades under sanctions, while imported components still support selected guidance, propulsion and electronics chains.',
  history: [
    '1980–1988 — Iran–Iraq War drives the modern emphasis on strategic depth and indigenous missile production.',
    '2017 — IRGC launches Zolfaghar missiles against Islamic State targets in Syria.',
    '2020 — Operation Martyr Soleimani strikes Ayn al-Asad airbase with ballistic missiles.',
    '2024 — Operations True Promise I and II employ missiles and one-way attack UAVs against Israel.',
  ],
  alliances: [
    'Axis of Resistance',
    'Syria',
    'Russia',
    'China',
    'Shanghai Cooperation Organisation',
  ],
  stats: {
    active_personnel: '610,000',
    reserve: '350,000',
    ballistic_stock: '2,000+ (est.)',
    uav_fleet: '1,000+ (est.)',
    combat_aircraft: '180+ (est.)',
    naval_major: '25+ vessels',
    defence_budget_est: '$10–20 bn',
    missile_production: 'Several hundred / year (est.)',
  },
  hide_locations: false,
  forces: {
    army: {
      name: 'Artesh Ground Forces',
      personnel: '~350,000 (incl. ~200k conscripts)',
      tanks: '1,500–2,000 (est.)',
      artillery: '4,000+ (est.)',
      helicopters: '~400 (est.)',
      note: 'Conscription-based; legacy US/Soviet equipment plus indigenous systems.',
    },
    navy: {
      name: 'Islamic Republic of Iran Navy (IRIN)',
      personnel: '~18,000',
      frigates: '~6',
      submarines: '3 Kilo + 20+ coastal (Ghadir/Fateh)',
      fast_attack: '100+ (est.)',
      note: 'Green-water navy focused on the Persian Gulf and Caspian.',
    },
    air_force: {
      name: 'IRIAF + Air Defence Force',
      personnel: '~37,000 (incl. ~15k AD)',
      combat_aircraft: '~300 (est.)',
      fighters: 'F-14, MiG-29, F-4, F-5/Kowsar, Su-24',
      note: 'Aging US fleet sustained by domestic overhaul; SAM umbrella expanding.',
    },
    irgc: {
      name: 'Islamic Revolutionary Guard Corps',
      personnel: '~190,000',
      navy: '~20,000 (fast craft, cruise missiles)',
      air: '~15,000 (missiles, drones)',
      missile_force: 'Largest in the region',
      note: 'Controls ballistic missiles, drones and regional partner networks.',
    },
  },
  ranks: {
    ground_air_officers: [
      ['2nd Lieutenant', 'Setvān Dovvom'],
      ['1st Lieutenant', 'Setvān Yekom'],
      ['Captain', 'Sarvān'],
      ['Major', 'Sargord'],
      ['Lieutenant Colonel', 'Sargord Yekom'],
      ['Colonel', 'Sarhang'],
      ['Brigadier General', 'Sartip'],
      ['Major General', 'Sartip Dovvom'],
      ['Lieutenant General', 'Sepahbod'],
      ['General', 'Arteshbod'],
    ],
    navy_officers: [
      ['Ensign', 'Nāvāban'],
      ['Lieutenant (JG)', 'Nāvāban Yekom'],
      ['Lieutenant', 'Nāvsarvān'],
      ['Lieutenant Commander', 'Nāvsargord'],
      ['Commander', 'Nāvsargord Yekom'],
      ['Captain', 'Nāvsarhang'],
      ['Rear Admiral (lower)', 'Daryādār'],
      ['Rear Admiral', 'Daryādār Yekom'],
      ['Vice Admiral', 'Daryāsalār'],
      ['Admiral', 'Daryāsalār Yekom'],
    ],
    enlisted: [
      ['Private', 'Sarbaz'],
      ['Private First Class', 'Sarbaz Yekom'],
      ['Corporal', 'Sarjukheh'],
      ['Sergeant', 'Goruhbān'],
      ['Staff Sergeant', 'Goruhbān Yekom'],
      ['Sergeant First Class', 'Rāhdār'],
      ['Master Sergeant', 'Rāhdār Yekom'],
      ['Sergeant Major', 'Sargord-e Yek'],
    ],
    note: 'IRGC uses the same rank titles as the regular Army; naval admiral titles apply to both fleets.',
  },
  updated_by: null,
  updated_at: now,
};
const weaponSeed: Array<[string, string, string, string, Record<string, string>]> = [
  [
    'Mines',
    'M-08',
    'Naval contact mine',
    "Contact mine forming part of Iran's naval mining stockpile; Hormuz contingency role.",
    {
      range: 'unknown',
      payload: '220 kg explosive',
      speed: 'unknown',
      stock: 'unknown',
      used: 'unknown',
      destroyed: 'unknown',
      guidance: 'unknown',
      cep: 'unknown',
      first_seen: '1970s (Russian M-08)',
      status: 'Operational',
    },
  ],
  [
    'Naval',
    'Karbala-class',
    'LCAC hovercraft',
    'Hovercraft landing craft for fast amphibious raids along the Gulf coast.',
    {
      range: 'unknown',
      payload: 'troops / light vehicles',
      speed: 'unknown',
      stock: '2 (est.)',
      used: 'unknown',
      destroyed: 'unknown',
      guidance: 'unknown',
      cep: 'unknown',
      first_seen: '2018',
      status: 'Operational',
    },
  ],
  [
    'Ballistic Missiles',
    'Shahab-3',
    'MRBM · liquid',
    'Road-mobile medium-range ballistic missile, the backbone of Iran’s strategic deterrent.',
    {
      range: '1,900 km',
      payload: '750–1,000 kg',
      speed: 'Mach 7 (est.)',
      stock: '100–200 (est.)',
      used: '20+ (est.)',
      destroyed: 'Unknown',
      guidance: 'Inertial + GPS',
      cep: '500 m (est.)',
      first_seen: '1998',
      status: 'Operational since 2003',
      combat_use: 'Used in 2024 ‘True Promise’ strikes against Israel.',
    },
  ],
  [
    'Ballistic Missiles',
    'Haj Qasem',
    'MRBM · liquid',
    'Medium-range ballistic missile with terminal guidance.',
    {
      range: '1,400 km',
      warhead: '500 kg',
      guidance: 'Inertial + terminal',
      status: 'Revealed 2020',
    },
  ],
  [
    'Ballistic Missiles',
    'Khorramshahr-4',
    'MRBM · liquid',
    'High-payload strategic missile.',
    { range: '2,000 km', warhead: '1,800 kg', guidance: 'Inertial + GPS', status: 'Tested 2023' },
  ],
  [
    'Ballistic Missiles',
    'Fateh-313',
    'SRBM · solid',
    'Fast-launch road-mobile missile.',
    {
      range: '500 km',
      warhead: '350 kg',
      guidance: 'Inertial + optoelectronic',
      status: 'Operational',
    },
  ],
  [
    'Ballistic Missiles',
    'Zolfaghar',
    'SRBM · solid',
    'Combat-proven solid-fuel SRBM.',
    { range: '700 km', warhead: '579 kg', guidance: 'Inertial + GPS', status: 'Operational' },
  ],
  [
    'Ballistic Missiles',
    'Emad',
    'MRBM · liquid',
    'Manoeuvring re-entry vehicle variant of Shahab-3.',
    { range: '1,700 km', warhead: '750 kg', guidance: 'Inertial + MaRV', status: 'Operational' },
  ],
  [
    'Cruise Missiles',
    'Soumar',
    'Land-attack cruise',
    'Long-range low-altitude land-attack missile.',
    { range: '2,000+ km', warhead: '450 kg', guidance: 'TERCOM + INS', status: 'Operational' },
  ],
  [
    'Cruise Missiles',
    'Paveh',
    'Land-attack cruise',
    'Modern precision cruise missile family.',
    { range: '1,650 km', warhead: '350 kg', guidance: 'INS + GPS + EO', status: 'Revealed 2023' },
  ],
  [
    'Cruise Missiles',
    'Abu Mahdi',
    'Anti-ship cruise',
    'Anti-ship missile for Gulf operations.',
    { range: '1,000+ km', warhead: '—', guidance: 'INS + active radar', status: 'Operational' },
  ],
  [
    'UAV / Drones',
    'Shahed-136',
    'Loitering munition',
    'One-way attack drone with mass-launch capability.',
    { range: '2,200 km', warhead: '40 kg', guidance: 'INS + GNSS', status: 'Combat-proven' },
  ],
  [
    'UAV / Drones',
    'Shahed-131',
    'Loitering munition',
    'Smaller one-way attack drone.',
    { range: '900 km', warhead: '15 kg', guidance: 'INS + GNSS', status: 'Combat-proven' },
  ],
  [
    'UAV / Drones',
    'Mohajer-6',
    'MALE UCAV',
    'Armed reconnaissance drone.',
    { range: '2,000 km', warhead: '—', guidance: 'SATCOM-capable', status: 'Operational' },
  ],
  [
    'UAV / Drones',
    'Mohajer-10',
    'MALE UCAV',
    'Long-endurance heavy-payload UCAV.',
    {
      range: '2,000 km',
      warhead: '300 kg payload',
      guidance: 'SATCOM + EO/IR',
      status: 'Revealed 2023',
    },
  ],
  [
    'UAV / Drones',
    'Shahed-129',
    'MALE UCAV',
    'Large long-endurance UCAV.',
    { range: '1,700 km', warhead: '—', guidance: 'SATCOM', status: 'Operational' },
  ],
  [
    'Air Defense',
    'Bavar-373',
    'Long-range SAM',
    'Indigenous long-range air-defence system.',
    { range: '200+ km', guidance: 'Active radar', status: 'Operational' },
  ],
  [
    'Air Defense',
    'Khordad-15',
    'Medium-range SAM',
    'Road-mobile medium-range SAM.',
    { range: '150 km', guidance: 'Radar + IR', status: 'Operational' },
  ],
  [
    'Air Defense',
    '3rd of Khordad',
    'Medium-range SAM',
    'Medium-range air-defence system.',
    { range: '75 km', guidance: 'Semi-active radar', status: 'Operational' },
  ],
  [
    'Aircraft',
    'F-14A Tomcat',
    'Fighter/interceptor',
    'US-built naval interceptor.',
    { 'max speed': 'Mach 2.34', role: 'Air superiority', status: 'Aging fleet' },
  ],
  [
    'Aircraft',
    'Kowsar',
    '4th-gen fighter',
    'Indigenous F-5-derived fighter.',
    { 'max speed': 'Mach 1.6', role: 'Multi-role', status: 'In production' },
  ],
  [
    'Naval',
    'Ghadir-class',
    'Coastal submarine',
    'Shallow-water coastal submarine.',
    { displacement: '120 t', role: 'Coastal denial', status: 'Operational' },
  ],
  [
    'Naval',
    'Peykaap III',
    'Fast attack craft',
    'High-speed missile craft.',
    { speed: '60+ kn', role: 'Coastal swarm', status: 'Operational' },
  ],
  [
    'Ground',
    'Karrar',
    'Main battle tank',
    'Modernised domestically produced tank.',
    { weight: '44 t', role: 'Armoured warfare', status: 'In production' },
  ],
  [
    'Ground',
    'Toofan-5',
    'ATGM',
    'Tandem-warhead anti-tank missile.',
    {
      range: '3,850 m',
      warhead: 'Tandem HEAT',
      guidance: 'Laser beam-riding',
      status: 'Operational',
    },
  ],
];
export const seedWeapons: WeaponRow[] = weaponSeed.map(
  ([category, name, type, description, specs], index) => ({
    id: `iran-${index + 1}`,
    country: 'Iran',
    category,
    name,
    type,
    description,
    specs,
    images:
      name === 'Shahab-3'
        ? [
            {
              url: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="640" height="360"%3E%3Crect width="640" height="360" fill="%23131c29"/%3E%3Cpath d="M80 280h480M260 250l45-170h30l45 170z" stroke="%234a7dff" stroke-width="12" fill="%231d2c43"/%3E%3C/svg%3E',
              caption: 'Shahab-3 field reference',
              uploader: 'system',
            },
          ]
        : [],
    created_by: null,
    updated_by: null,
    version: 1,
    created_at: now,
    updated_at: now,
  }),
);

const load = <T>(key: string, fallback: T): T => {
  try {
    return JSON.parse(localStorage.getItem(key) ?? '') as T;
  } catch {
    return fallback;
  }
};
const save = (key: string, value: unknown): void =>
  localStorage.setItem(key, JSON.stringify(value));
// E2E/Demo: Ziel-Standorte für Shahab-3
const seedWithTargets = seedWeapons.map((w) =>
  w.name === 'Shahab-3'
    ? {
        ...w,
        targets: [
          { place: 'Ayn al-Asad AB, Iraq', lat: 33.808, lng: 42.441, date: 'Jan 2020', note: 'Operation Martyr Soleimani' },
          { place: 'Tel Aviv area, Israel', lat: 32.085, lng: 34.782, date: 'Apr 2024', note: 'True Promise I' },
        ],
      }
    : w,
);
export const demoStore = {
  forums: (): ForumRow[] => load('zenith:forums', seedForums),
  posts: (): PostRow[] => load('zenith:posts', [seedPost]),
  comments: (postId: string): CommentRow[] =>
    load<CommentRow[]>('zenith:comments', []).filter((item) => item.post_id === postId),
  addComment: (
    postId: string,
    author: string,
    text: string,
    parentId: string | null,
    media: unknown[] = [],
  ): CommentRow => {
    const all = load<CommentRow[]>('zenith:comments', []);
    const item: CommentRow = {
      id: makeId(),
      post_id: postId,
      parent_id: parentId,
      author,
      text,
      media: media as CommentRow['media'],
      votes: 0,
      created_at: new Date().toISOString(),
    };
    save('zenith:comments', [...all, item]);
    return item;
  },
  votePost: (id: string, dir: -1 | 1): number => {
    const list = demoStore
      .posts()
      .map((post) => (post.id === id ? { ...post, votes: post.votes + dir } : post));
    save('zenith:posts', list);
    return list.find((post) => post.id === id)?.votes ?? 0;
  },
  addForum: (name: string, icon: string, creator: string, description: string): ForumRow => {
    const item: ForumRow = {
      id: makeId(),
      name,
      icon,
      creator,
      description,
      members: 1,
      is_section: false,
      created_at: new Date().toISOString(),
    };
    save('zenith:forums', [item, ...demoStore.forums()]);
    return item;
  },
  logModeration: (kind: 'flags' | 'reports', value: object): void =>
    save(`zenith:${kind}`, [...load<object[]>(`zenith:${kind}`, []), value]),
  countries: (): CountryRow[] => load('zenith:countries', [seedCountry]),
  weapons: (): WeaponRow[] => load('zenith:weapons', seedWithTargets),
  history: (): WikiHistoryRow[] => load('zenith:wiki-history', []),
  updateCountry: (id: string, patch: Partial<CountryRow>, editor: string): CountryRow => {
    const before = demoStore.countries().find((x) => x.id === id)!;
    const after = { ...before, ...patch, updated_by: editor, updated_at: new Date().toISOString() };
    save(
      'zenith:countries',
      demoStore.countries().map((x) => (x.id === id ? after : x)),
    );
    save('zenith:wiki-history', [
      ...demoStore.history(),
      {
        id: makeId(),
        weapon_id: null,
        country_id: id,
        editor,
        change: { before, after },
        created_at: after.updated_at,
      },
    ]);
    return after;
  },
  saveWeapon: (weapon: WeaponRow, editor: string): WeaponRow => {
    const all = demoStore.weapons();
    const before = all.find((x) => x.id === weapon.id);
    const after = {
      ...weapon,
      updated_by: editor,
      version: before ? before.version + 1 : 1,
      updated_at: new Date().toISOString(),
    };
    save(
      'zenith:weapons',
      before ? all.map((x) => (x.id === after.id ? after : x)) : [...all, after],
    );
    save('zenith:wiki-history', [
      ...demoStore.history(),
      {
        id: makeId(),
        weapon_id: after.id,
        country_id: null,
        editor,
        change: { before, after },
        created_at: after.updated_at,
      },
    ]);
    return after;
  },
};
